import * as Sentry from '@sentry/react'
import { installChunkRetry } from './core/services/init/initializers/chunk-retry'
import { platform } from './core/services/device/platform'
import { renderInitErrorPage } from './application/components/ErrorPage'
import { getInitTracker } from './core/services/init/tracking/init-tracker'
import { filesystem } from './core/services/init/index'
import startZipDownload from './core/services/filesystem/startDownload'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STEP 0 — Instalar chunk retry antes de cualquier import dinámico
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
installChunkRetry()

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// APPLICATION INIT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
startApp()

async function startApp (): Promise<void> {
  try {
    // STEP 1 — Pre-inicialización (plataforma, sesión, discovery, filesystem)
    const { default: preInit } = await import('./core/services/init/index')
    const initParams = await preInit()

    // STEP 2 — Carga secuencial de módulos estáticos (con retry)
    const { loadStaticModules } = await import('./core/services/init/load-static-modules')
    const { timings, failedModules } = await loadStaticModules(initParams)

    // STEP 3 — Validar resultado según plataforma
    if (failedModules.length > 0 && platform !== 'web') {
      handleMobileModuleFailure(initParams, failedModules, timings)
      return
    }

    // STEP 4 — Bootstrap React
    const tracker = getInitTracker()
    tracker.startStep('bootstrap')
    const { initPlatform } = await import('./bootstrap')
    initPlatform(initParams)
    tracker.endStep('bootstrap', { moduleTimings: timings })

    reportBootstrapSuccess(timings)
  } catch (err) {
    handleFatalError(err)
  }
}

// ─── Error Handlers ──────────────────────────────────────────────────────────

function handleMobileModuleFailure (
  initParams: { discoveredMicrofronts: any[] },
  failedModules: string[],
  timings: Array<{ name: string, durationMs: number, success: boolean }>
): void {
  const failedMicrofronts = failedModules
    .map(name => initParams.discoveredMicrofronts.find(m => m.mfe_name.toLowerCase() === name.toLowerCase()))
    .filter(Boolean)

  Sentry.captureEvent({
    message: 'Mobile Init - Module Load Failure (showing error page)',
    level: 'error',
    tags: { platform, init_phase: 'static_modules', init_result: 'partial_failure' },
    extra: { failedModules, failedCount: failedModules.length, totalModules: timings.length, moduleTimings: timings }
  })

  renderInitErrorPage({ failedMicrofronts, error: new Error(`Failed to load modules: ${failedModules.join(', ')}`) })
}

function handleFatalError (err: unknown): void {
  console.error('Error during pre-initialization:', err)
  getInitTracker().reportFatalFailure(err)
  renderInitErrorPage({ error: err instanceof Error ? err : new Error(String(err)) })
}

function reportBootstrapSuccess (timings: Array<{ name: string, durationMs: number, success: boolean }>): void {
  Sentry.captureEvent({
    message: 'Mobile Init - Full Bootstrap Complete',
    level: 'info',
    tags: { platform, init_phase: 'complete', init_result: 'success' },
    extra: {
      modulesLoaded: timings.filter(m => m.success).length,
      totalModuleLoadTimeMs: timings.reduce((sum, m) => sum + m.durationMs, 0).toFixed(0),
      moduleTimings: timings
    }
  })
}

// ─── Export: Re-descarga de módulos fallidos (usado por ErrorPage) ────────────

export async function downloadFailedModules (failedMicrofronts: any[], onProgress?: (progress: number) => void): Promise<void> {
  for (let i = 0; i < failedMicrofronts.length; i++) {
    const mfe = failedMicrofronts[i]
    await startZipDownload({
      type: 'modules',
      directory: filesystem?.getPath('modules', { mfe_id: mfe.mfe_id, mfe_version: mfe.version }),
      filename: mfe.data.zip_file,
      url: `${process.env.MFE_SOURCES_URL}/${mfe.location}${mfe.data.zip_file}`
    })
    onProgress?.((i + 1) / failedMicrofronts.length * 100)
  }
}
